/**
 * Bildschirmaufnahme über getDisplayMedia.
 *
 * Gearbeitet wird nur, wenn tatsächlich ein neues Bild vorliegt – der alte
 * Electron-Sender lief über ein festes 'sendInterval' und hat auch bei
 * unverändertem Bildschirm jedes Mal das komplette Canvas ausgelesen.
 *
 * Der Takt darf dabei nicht am Rendering des Tabs hängen: Ein Sender im
 * Hintergrund bekommt weder requestVideoFrameCallback noch ungedrosselte
 * Timer, der geteilte Bildschirm läuft aber weiter. Deshalb zwei Wege:
 *
 *  - MediaStreamTrackProcessor (Chrome, Edge): Die Bilder kommen als Strom
 *    direkt aus dem Track, gelesen in einer Promise-Schleife. Kein Timer,
 *    kein Rendering – von Sichtbarkeit unabhängig.
 *  - Sonst ein <video> mit requestVideoFrameCallback, ergänzt um einen
 *    Worker als zweite Taktquelle. Timer in einem dedizierten Worker
 *    unterliegen der Hintergrund-Drosselung nicht.
 *
 * Beide Quellen münden in denselben entprellten tick(); die Wächter 'busy'
 * und der Soll-Zeitpunkt verhindern doppelte Arbeit, wenn beide zugleich feuern.
 */

import { MAX_CAPTURE_EDGE, MAX_FPS } from '../../shared/constants.ts';

/** Nur in lib.webworker.d.ts deklariert, hier aber im Fenster gebraucht. */
declare const MediaStreamTrackProcessor: {
  new (init: { track: MediaStreamTrack }): { readable: ReadableStream<VideoFrame> };
} | undefined;

/**
 * Um diesen Faktor läuft die Quelle schneller als die Zielrate, damit die
 * Ratenbegrenzung nicht auf jedes einzelne Bild angewiesen ist.
 */
const SOURCE_RATE_FACTOR = 2;

/**
 * Taktgeber für den Fallbackpfad. Läuft als eigener Worker, weil ein
 * setInterval im Fenster bei verdecktem Tab auf ein Ereignis pro Sekunde und
 * nach einigen Minuten auf eines pro Minute gedrosselt wird.
 */
const TICKER_SOURCE = `let id = 0;
onmessage = (e) => {
  clearInterval(id);
  if (e.data > 0) id = setInterval(() => postMessage(0), e.data);
};`;

export interface CaptureFrame {
  data: ImageData;
  canvas: OffscreenCanvas;
  width: number;
  height: number;
  /**
   * Dauer von drawImage und getImageData zusammen.
   * Bei 4K wandern dabei 33 MB von der GPU zurück in den Hauptspeicher – ein
   * Posten, der ohne diese Zahl in keiner Statistik auftaucht.
   */
  captureMs: number;
}

type FrameHandler = (frame: CaptureFrame) => void | Promise<void>;

export class ScreenCapture {
  private video = document.createElement('video');
  private canvas = new OffscreenCanvas(1, 1);
  private ctx = this.canvas.getContext('2d', { willReadFrequently: true })!;
  private stream: MediaStream | null = null;
  private running = false;
  /**
   * Läuft gerade ein start()? Zwischen dem Aufruf und der Antwort steht der
   * Auswahldialog des Browsers – sekundenlang, und in dieser Zeit ist `running`
   * noch false.
   */
  private starting = false;
  private busy = false;
  /** Frühester Zeitpunkt für das nächste Bild – fortgeschrieben, nicht neu gesetzt. */
  private nextDueAt = 0;

  /** Wie oft ein angebotenes Bild wegen der Ratenbegrenzung übergangen wurde. */
  skipped = 0;

  /** Nur im Fallbackpfad belegt. */
  private ticker: Worker | null = null;
  private tickerUrl = '';
  /** Verhindert zwei parallele requestVideoFrameCallback-Ketten. */
  private rvfcPending = false;

  /** Nur im Track-Pfad belegt; beendet die Leseschleife beim Stoppen. */
  private reader: ReadableStreamDefaultReader<VideoFrame> | null = null;

  private targetFps = MAX_FPS;

  /**
   * Obergrenze für die längere Bildkante; wirkt erst beim nächsten start().
   * Überschreibbar, um Auflösungen vergleichen zu können.
   */
  maxEdge = MAX_CAPTURE_EDGE;

  /** Wird gerufen, wenn sich die Auflösung der Quelle ändert. */
  onResize: ((width: number, height: number) => void) | null = null;

  /** Wird einmal gerufen, wenn die Freigabe endet – per Knopf oder Browserleiste. */
  onStop: (() => void) | null = null;

  /**
   * Rückfrage vor der Aufnahme: Kann der Verbraucher gerade ein Bild annehmen?
   *
   * Ohne sie würde bei überlaufender Verarbeitung das ganze Bild ausgelesen –
   * bei 4K ein Rückkopieren von 33 MB – nur um es anschließend zu verwerfen.
   * Das ist der teuerste Weg, nichts zu erreichen.
   */
  canAccept: (() => boolean) | null = null;

  constructor(private onFrame: FrameHandler) {
    this.video.muted = true;
    this.video.playsInline = true;
  }

  get active(): boolean {
    return this.running;
  }

  get width(): number {
    return this.canvas.width;
  }

  get height(): number {
    return this.canvas.height;
  }

  /** Ziel-Bildrate; der Server kann sie über setRate herunterregeln. */
  get fps(): number {
    return this.targetFps;
  }

  set fps(value: number) {
    this.targetFps = value;
    this.ticker?.postMessage(this.tickInterval());
  }

  async start(): Promise<void> {
    // Ein zweiter Start würde den vorhandenen Strom bloß überschreiben – die
    // erste Aufnahme liefe unbemerkt weiter, samt Aufnahmeanzeige des Browsers,
    // und im Track-Pfad lägen zwei Leseschleifen auf demselben Canvas.
    if (this.running || this.starting) return;
    this.starting = true;
    try {
      await this.open();
    } finally {
      this.starting = false;
    }
  }

  private async open(): Promise<void> {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        // Doppelte Zielrate: Liefert die Quelle genau im Zieltakt, trifft jedes
        // Bild grenzwertig ein und schon geringer Jitter lässt es durchfallen –
        // dann käme erst das übernächste an und die Rate halbierte sich. Mit
        // der doppelten Rate hat die Ratenbegrenzung Auswahl; die überzähligen
        // Bilder kosten nichts, sie werden vor jeder Verarbeitung verworfen.
        frameRate: { ideal: this.targetFps * SOURCE_RATE_FACTOR },
        // Nur eine Obergrenze, kein 'ideal': Ein Zielwert fordert den Browser
        // auf, darauf hin zu skalieren – notfalls auch hoch, was für ein
        // kleines Fenster nur unscharfe Pixel und mehr Rechenzeit brächte.
        // 'max' greift ausschließlich nach unten. Beide Kanten bekommen
        // denselben Deckel, damit gedrehte Bildschirme gleich behandelt
        // werden; das Seitenverhältnis bleibt erhalten.
        width: { max: this.maxEdge },
        height: { max: this.maxEdge },
        // Ohne Cursor im Bild bliebe im Browser kein Weg, ihn zu übertragen –
        // die globale Mausposition ist nur nativ zugänglich.
        cursor: 'always',
      } as MediaTrackConstraints,
      audio: false,
    });

    // Reste eines früheren Versuchs stoppen, bevor die Kennung überschrieben
    // wird: Ein Strom, den niemand mehr kennt, nimmt weiter den Bildschirm auf.
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = stream;

    const track = stream.getVideoTracks()[0];
    track?.addEventListener('ended', () => this.stop());
    this.running = true;
    this.nextDueAt = 0;
    this.skipped = 0;

    if (track && typeof MediaStreamTrackProcessor !== 'undefined') {
      // Die Maße stehen schon in den Track-Einstellungen; ohne sie zeigte die
      // Statusanzeige bis zum ersten Bild eine leere Fläche an.
      const settings = track.getSettings();
      this.applySize(settings.width ?? 0, settings.height ?? 0);
      void this.readTrack(track);
      return;
    }

    this.video.srcObject = stream;
    await this.video.play();
    this.applySize(this.video.videoWidth, this.video.videoHeight);
    this.startTicker();
    this.scheduleNext();
  }

  stop(): void {
    // Knopf und 'ended'-Ereignis der Browserleiste laufen beide hier durch;
    // gemeldet wird das Ende trotzdem nur einmal.
    const wasRunning = this.running;
    this.running = false;

    void this.reader?.cancel().catch(() => {});
    this.reader = null;
    this.stopTicker();

    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.video.srcObject = null;

    if (wasRunning) this.onStop?.();
  }

  // -------------------------------------------------------------------------
  // Taktquellen
  // -------------------------------------------------------------------------

  /** Track-Pfad: Bilder aus dem Strom lesen, bis gestoppt wird. */
  private async readTrack(track: MediaStreamTrack): Promise<void> {
    const processor = new MediaStreamTrackProcessor!({ track });
    this.reader = processor.readable.getReader();

    try {
      while (this.running) {
        const { value, done } = await this.reader.read();
        if (done) break;
        try {
          await this.tick(value, value.displayWidth, value.displayHeight);
        } finally {
          // Auch ein durch das Ratenlimit verworfenes Bild muss freigegeben
          // werden, sonst blockiert der Strom nach wenigen Frames.
          value.close();
        }
      }
    } catch {
      // Abbruch beim Stoppen – kein Fehlerfall.
    }
  }

  /** Fallbackpfad: nach jedem verarbeiteten Bild das nächste anfordern. */
  private scheduleNext(): void {
    if (!this.running || this.rvfcPending) return;
    // Ältere Browser kennen den Bildtakt nicht; dort trägt der Worker allein.
    if (typeof this.video.requestVideoFrameCallback !== 'function') return;
    this.rvfcPending = true;
    this.video.requestVideoFrameCallback(() => {
      this.rvfcPending = false;
      void this.tick(this.video, this.video.videoWidth, this.video.videoHeight).then(() =>
        this.scheduleNext(),
      );
    });
  }

  private startTicker(): void {
    this.tickerUrl = URL.createObjectURL(new Blob([TICKER_SOURCE], { type: 'text/javascript' }));
    this.ticker = new Worker(this.tickerUrl);
    // Der Worker taktet blind mit; im Vordergrund verpufft er am Ratenlimit,
    // weil requestVideoFrameCallback schneller da ist.
    this.ticker.onmessage = () => {
      void this.tick(this.video, this.video.videoWidth, this.video.videoHeight);
      // Der Bildtakt steht im Hintergrund still – die Kette neu anstoßen,
      // damit sie beim Zurückwechseln sofort wieder greift.
      this.scheduleNext();
    };
    this.ticker.postMessage(this.tickInterval());
  }

  private stopTicker(): void {
    this.ticker?.terminate();
    this.ticker = null;
    if (this.tickerUrl) URL.revokeObjectURL(this.tickerUrl);
    this.tickerUrl = '';
    this.rvfcPending = false;
  }

  private tickInterval(): number {
    return Math.max(16, Math.round(1000 / this.targetFps));
  }

  // -------------------------------------------------------------------------
  // Aufnahme
  // -------------------------------------------------------------------------

  /**
   * Ein Bild übernehmen – gleich aus welcher Taktquelle.
   * Plant nichts nach: Das bleibt Sache der jeweiligen Quelle.
   */
  private async tick(source: CanvasImageSource, sourceWidth: number, sourceHeight: number): Promise<void> {
    if (!this.running) return;

    // Ratenbegrenzung: getDisplayMedia liefert oft mehr Bilder als gewünscht.
    //
    // Der Soll-Zeitpunkt wird fortgeschrieben statt vom tatsächlichen Eintreffen
    // aus neu berechnet, und er hat Spielraum: Die Quelle läuft selbst schon mit
    // der Zielrate, also träfe ein Bild mit ein paar Millisekunden Jitter sonst
    // regelmäßig knapp zu früh ein, fiele heraus – und die Rate halbierte sich,
    // weil erst das übernächste Bild angenommen würde.
    const now = performance.now();
    if (this.busy) return;
    const minInterval = 1000 / this.targetFps;
    if (now < this.nextDueAt - minInterval * 0.25) {
      this.skipped++;
      return;
    }
    // Abgelehnte Bilder schreiben den Soll-Zeitpunkt nicht fort: Sobald der
    // Verbraucher wieder kann, soll das nächste Bild sofort durchgehen und
    // nicht erst nach einem weiteren Intervall.
    if (this.canAccept && !this.canAccept()) return;

    this.nextDueAt = Math.max(now, this.nextDueAt + minInterval);
    this.busy = true;

    try {
      this.applySize(sourceWidth, sourceHeight);
      const { width, height } = this.canvas;
      if (width > 0 && height > 0) {
        this.ctx.drawImage(source, 0, 0);
        const data = this.ctx.getImageData(0, 0, width, height);
        // Bewusst nicht abwarten: Der Handler arbeitet synchron, bis er in die
        // Kodierung geht – danach soll längst das nächste Bild geholt werden.
        // Was er dabei aus diesem Canvas braucht, hat er bis dahin kopiert.
        const pending = this.onFrame({
          data,
          canvas: this.canvas,
          width,
          height,
          captureMs: performance.now() - now,
        });
        void Promise.resolve(pending).catch((err) => console.error('[capture] Frame-Fehler:', err));
      }
    } finally {
      this.busy = false;
    }
  }

  private applySize(w: number, h: number): void {
    if (w > 0 && h > 0 && (this.canvas.width !== w || this.canvas.height !== h)) {
      this.canvas.width = w;
      this.canvas.height = h;
      this.onResize?.(w, h);
    }
  }
}
